import { deleteActivityThunk, fetchActivitiesThunk, saveActivityThunk } from '@spike/activities-action';
import { Activity, ActivityPagination, ActivityType } from '@spike/activity-model';
import { Period } from '@spike/model';
import { showSuccessThunk } from '@spike/notifications-action';
import { ALL_ACTIVITY_TYPES } from 'components/Activities/Icon/ActivityIcon';
import Pet from '@spike/pet-model';
import { useApiClientWrapper } from 'hooks';
import React from 'react';
import { dispatch } from 'store';

const PAGE_SIZE = 10;

const isSameActivity = (left: Activity, right: Activity): boolean => {
    return left.id === right.id || left.uuid === right.uuid;
};

const sortActivitiesByTimelineOrder = (items: Array<Activity>): Array<Activity> => {
    return [...items].sort((left, right) => {
        const dateDiff = right.date.valueOf() - left.date.valueOf();

        if (dateDiff !== 0) {
            return dateDiff;
        }

        const createdAtDiff = right.createdAt.valueOf() - left.createdAt.valueOf();

        if (createdAtDiff !== 0) {
            return createdAtDiff;
        }

        return right.id - left.id;
    });
};

const matchesTimelineFilters = (
    activity: Activity,
    petId: number,
    bookingId: number | undefined,
    activityTypes: Array<ActivityType>,
    datePeriod: Period | null
): boolean => {
    const matchesPet = activity.pets.some(pet => pet.id === petId);

    if (!matchesPet) {
        return false;
    }

    if (bookingId !== undefined && activity.bookingId !== bookingId) {
        return false;
    }

    const hasTypeFilter = activityTypes.length > 0 && activityTypes.length < ALL_ACTIVITY_TYPES.length;

    if (hasTypeFilter && !activityTypes.includes(activity.type)) {
        return false;
    }

    if (datePeriod && !activity.date.isBetween(datePeriod.from, datePeriod.to, undefined, '[]')) {
        return false;
    }

    return true;
};

interface ActivityTimelineContextProps {
    // Data
    pet: Pet;
    pets: Array<Pet>;
    isPetEnabled: boolean;
    customerId: number;
    bookingId?: number;
    activities: Array<Activity>;
    createActivityDateLimits: Period | undefined;
    createActivityAllowedDates: Array<string> | undefined;
    createActivityDateTimeRanges: Record<string, Array<Period>> | undefined;
    isLoading: boolean;
    zIndex?: number;

    // Pagination
    page: number;
    totalPages: number;

    isFetching: boolean;
    isFirstRender: boolean;

    // Type filter
    activityTypes: Array<ActivityType>;
    activityTypesChangeHandler: ActivityTypesChangeHandler;

    // Date period
    datePeriod: Period | null;
    datePeriodChangeHandler: DatePeriodChangeHandler;

    // Activity Form
    showActivityForm: boolean;
    activityType: ActivityType | undefined;
    activityToEdit: Activity | undefined;
    openActivityForm: OpenActivityFormHandler;
    closeActivityForm: CloseActivityFormHandler;

    // Actions
    fetchActivities: FetchActivitiesHandler;
    deleteActivity: DeleteActivityHandler;
    loadMoreActivities: () => void;
}

/* eslint-disable no-unused-vars */
type ActivityTypesChangeHandler = (types: Array<ActivityType>) => void;
type DatePeriodChangeHandler = (period: Period | null) => void;
type OpenActivityFormHandler = (type?: ActivityType, activity?: Activity) => void;
type CloseActivityFormHandler = (savedActivity?: Activity) => void;
type FetchActivitiesHandler = (page: number, types?: Array<ActivityType>, datePeriod?: Period | null) => void;
type DeleteActivityHandler = (activity: Activity) => void;
/* eslint-enable no-unused-vars */

const ActivityTimelineContext = React.createContext<ActivityTimelineContextProps | null>(null);

interface ActivityTimelineProviderProps {
    pet: Pet;
    pets: Array<Pet>;
    customerId: number;
    bookingId?: number;
    createActivityDateLimits?: Period;
    createActivityAllowedDates?: Array<string>;
    createActivityDateTimeRanges?: Record<string, Array<Period>>;
    zIndex?: number;
    children: React.ReactNode;
}

export const ActivityTimelineProvider = ({
    pet,
    pets,
    customerId,
    bookingId,
    createActivityDateLimits,
    createActivityAllowedDates,
    createActivityDateTimeRanges,
    zIndex,
    children
}: ActivityTimelineProviderProps) => {
    const apiClientWrapper = useApiClientWrapper();

    const [activities, setActivities] = React.useState<Array<Activity>>([]);
    const [pagination, setPagination] = React.useState<ActivityPagination | undefined>(undefined);
    const [loading, setLoading] = React.useState<boolean>(false);

    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isFirstRender, setIsFirstRender] = React.useState<boolean>(true);

    const [page, setPage] = React.useState<number>(1);
    const [datePeriod, setDatePeriod] = React.useState<Period | null>(null);
    const [activityTypes, setActivityTypes] = React.useState<Array<ActivityType>>(ALL_ACTIVITY_TYPES);

    const [showActivityForm, setShowActivityForm] = React.useState(false);
    const [activityType, setActivityType] = React.useState<ActivityType | undefined>();
    const [activityToEdit, setActivityToEdit] = React.useState<Activity | undefined>();

    const isPetEnabled = React.useMemo(() => {
        return pet.id !== undefined && !pet.deceased && pet.active;
    }, [pet]);

    /*
     * ---- Actions ----
     */

    const fetchActivities = React.useCallback(
        async (page = 1, types?: Array<ActivityType>, datePeriod?: Period | null) => {
            setIsFetching(true);
            setLoading(true);

            try {
                const nextActivities = await dispatch(
                    fetchActivitiesThunk(
                        apiClientWrapper,
                        Number(pet.id),
                        page,
                        PAGE_SIZE,
                        bookingId,
                        types && types.length > 0 && types.length < ALL_ACTIVITY_TYPES.length ? types : undefined,
                        datePeriod ?? undefined
                    )
                );

                setActivities(prev =>
                    page === 1 ? nextActivities.activities : [...prev, ...nextActivities.activities]
                );
                setPagination(nextActivities.pagination);
            } finally {
                setLoading(false);
            }

            if (isFirstRender) {
                setIsFirstRender(false);
            }

            setIsFetching(false);
        },
        [page, pet.id, bookingId, apiClientWrapper, isFirstRender]
    );

    const deleteActivity = React.useCallback(
        async (activity: Activity) => {
            if (activity.pets.length === 1) {
                // If activity has only one pet, delete it
                await dispatch(deleteActivityThunk(apiClientWrapper, activity.id)).catch(() => {
                    return null;
                });

                setActivities(prev => prev.filter(item => item.id !== activity.id));
            } else {
                // If activity has multiple pets, remove the pet from the activity
                const savedActivity = await dispatch(
                    saveActivityThunk(apiClientWrapper, {
                        ...activity,
                        petIds: activity.pets.filter(p => p.id !== pet.id).map(pet => pet.id)
                    })
                );

                if (savedActivity) {
                    setActivities(prev => {
                        const updated = prev.map(item =>
                            item.uuid === savedActivity.uuid ? { ...savedActivity } : item
                        );

                        return updated.filter(item => item.pets.some(p => p.id === pet.id));
                    });
                }
            }

            dispatch(showSuccessThunk('Activity deleted successfully'));
            fetchActivities();
        },
        [pet.id, apiClientWrapper, fetchActivities]
    );

    const openActivityForm = React.useCallback((type?: ActivityType, activity?: Activity) => {
        setShowActivityForm(true);
        setActivityType(type);
        setActivityToEdit(activity);
    }, []);

    const closeActivityForm = React.useCallback(
        (savedActivity?: Activity) => {
            setShowActivityForm(false);
            setActivityType(undefined);
            setActivityToEdit(undefined);

            if (!savedActivity) {
                return;
            }

            const petId = Number(pet.id);
            const shouldIncludeActivity = matchesTimelineFilters(
                savedActivity,
                petId,
                bookingId,
                activityTypes,
                datePeriod
            );

            setActivities(prev => {
                const nextActivities = prev.filter(activity => !isSameActivity(activity, savedActivity));

                if (!shouldIncludeActivity) {
                    return nextActivities;
                }

                return sortActivitiesByTimelineOrder([savedActivity, ...nextActivities]);
            });

            if (page !== 1) {
                setPage(1);
                return;
            }

            void fetchActivities(1, activityTypes, datePeriod);
        },
        [pet.id, bookingId, activityTypes, datePeriod, page, fetchActivities]
    );

    const loadMoreActivities = React.useCallback(() => {
        setPage(prev => prev + 1);
    }, []);

    /*
     * ---- Event Handlers ----
     */

    const activityTypesChangeHandler = React.useCallback((types: Array<ActivityType>) => {
        setActivityTypes(types);
    }, []);

    const datePeriodChangeHandler = React.useCallback((period: Period | null) => {
        setDatePeriod(period);
    }, []);

    /*
     * ---- Effects ----
     */

    React.useEffect(() => {
        fetchActivities(page, activityTypes, datePeriod);
    }, [page, pet.id, activityTypes, datePeriod]);

    /*
     * ---- Error Handling ----
     */

    if (!pet.id) {
        throw new Error('Pet ID is required');
    }

    return (
        <ActivityTimelineContext.Provider
            value={{
                pet,
                pets,
                isPetEnabled,
                customerId,
                bookingId,
                activities,
                isFetching,
                createActivityDateLimits,
                createActivityAllowedDates,
                createActivityDateTimeRanges,
                isLoading: loading,
                zIndex,

                page,
                totalPages: pagination?.totalPages ?? page,

                isFirstRender,

                activityTypes,
                activityTypesChangeHandler,

                datePeriod,
                datePeriodChangeHandler,

                fetchActivities,
                deleteActivity,
                loadMoreActivities,

                showActivityForm,
                activityType,
                activityToEdit,
                openActivityForm,
                closeActivityForm
            }}
        >
            {children}
        </ActivityTimelineContext.Provider>
    );
};

export const useActivityTimeline = (): ActivityTimelineContextProps => {
    const context = React.useContext(ActivityTimelineContext);

    if (!context) {
        throw new Error('useActivityTimeline must be used within an ActivityTimelineProvider');
    }

    return context;
};
